import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Button, 
  IconButton, 
  TextField, 
  Box, 
  Typography, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../../utils/IconHandler';
import SitesService from '../../../../services/SitesService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Colores predefinidos - Material Design Palette
const PREDEFINED_COLORS = [
  { value: '#F44336', label: 'Rojo', name: 'Red' },
  { value: '#E91E63', label: 'Rosa', name: 'Pink' },
  { value: '#9C27B0', label: 'Morado', name: 'Purple' },
  { value: '#673AB7', label: 'Morado Oscuro', name: 'Deep Purple' },
  { value: '#3F51B5', label: 'Índigo', name: 'Indigo' },
  { value: '#2196F3', label: 'Azul', name: 'Blue' },
  { value: '#03A9F4', label: 'Azul Claro', name: 'Light Blue' },
  { value: '#00BCD4', label: 'Cyan', name: 'Cyan' },
  { value: '#009688', label: 'Verde Azulado', name: 'Teal' },
  { value: '#4CAF50', label: 'Verde', name: 'Green' },
  { value: '#8BC34A', label: 'Verde Claro', name: 'Light Green' },
  { value: '#CDDC39', label: 'Lima', name: 'Lime' },
  { value: '#FFEB3B', label: 'Amarillo', name: 'Yellow' },
  { value: '#FFC107', label: 'Ámbar', name: 'Amber' },
  { value: '#FF9800', label: 'Naranja', name: 'Orange' },
  { value: '#FF5722', label: 'Naranja Oscuro', name: 'Deep Orange' },
  { value: '#795548', label: 'Marrón', name: 'Brown' },
  { value: '#9E9E9E', label: 'Gris', name: 'Grey' },
  { value: '#607D8B', label: 'Gris Azulado', name: 'Blue Grey' }
];

const TagsTab = ({ tags, onTagsChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '' });

  const handleCreate = () => {
    setFormData({ name: '', color: '' });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setFormData({ name: tag.name, color: tag.color || '' });
    setIsEditModalOpen(true);
  };

  const handleDelete = (tag) => {
    setSelectedTag(tag);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = {
        name: formData.name,
        color: formData.color || null
      };
      
      if (isCreateModalOpen) {
        await SitesService.createSiteProductTag(data);
      } else {
        await SitesService.updateSiteProductTag(selectedTag.id, data);
      }
      
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setSelectedTag(null);
      onTagsChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar tag');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      await SitesService.deleteSiteProductTag(selectedTag.id);
      setIsDeleteDialogOpen(false);
      setSelectedTag(null);
      onTagsChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar tag');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 80 
    },
    { 
      field: 'name', 
      headerName: t('name') || 'Nombre', 
      flex: 1 
    },
    {
      field: 'color',
      headerName: t('color') || 'Color',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.color ? (
            <Chip 
              label={params.row.color} 
              sx={{ 
                backgroundColor: params.row.color,
                color: '#fff',
                fontWeight: 'bold'
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton 
            size="small" 
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('tags') || 'Tags'}</Typography>
        <Button 
          variant="contained" 
          startIcon={getIcon('add')} 
          onClick={handleCreate}
          disabled={loading}
        >
          {t('create_tag') || 'Crear Tag'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataGrid
        rows={tags}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } }
        }}
      />

      {/* Modal Crear/Editar */}
      <Dialog 
        open={isCreateModalOpen || isEditModalOpen} 
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedTag(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isCreateModalOpen ? (t('create_tag') || 'Crear Tag') : (t('edit_tag') || 'Editar Tag')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('name') || 'Nombre'}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('color') || 'Color'}</InputLabel>
              <Select
                value={formData.color || ''}
                label={t('color') || 'Color'}
                onChange={(e) => setFormData({ ...formData, color: e.target.value || null })}
                renderValue={(selected) => {
                  if (!selected) return t('none') || 'Ninguno';
                  const color = PREDEFINED_COLORS.find(c => c.value === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: selected,
                          border: '1px solid #ccc'
                        }}
                      />
                      <Typography>{color ? color.label : selected}</Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        backgroundColor: 'transparent'
                      }}
                    />
                    <Typography>{t('none') || 'Ninguno'}</Typography>
                  </Box>
                </MenuItem>
                {PREDEFINED_COLORS.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color.value,
                          border: '1px solid #ccc'
                        }}
                      />
                      <Typography>{color.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.color && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('preview') || 'Vista previa:'}</Typography>
                <Chip 
                  label={formData.color} 
                  sx={{ 
                    backgroundColor: formData.color,
                    color: '#fff',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedTag(null);
          }}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !formData.name}>
            {t('save') || 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Eliminar */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{t('confirm_delete') || 'Confirmar Eliminación'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirm_delete_tag', { name: selectedTag?.name }) || `¿Estás seguro de que deseas eliminar el tag "${selectedTag?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={loading}>
            {t('delete') || 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagsTab;
